import { memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { dialog, storage } from 'app/utils';
import { useLoader, useMountEffect } from 'app/hooks';
import { Breadcrumb } from 'app/components';
import { Button, IButton, Icon } from 'app/components/common';
import { Table, ITable, IPaging } from 'app/components/data';
import { serializers } from 'app/components/utils';
import { confirmations } from 'app/services';
import { IConfirmation } from 'app/models';

import Rejection from './rejection';

const stateKey = 'modules.confirmations';
const loader = (params: IPaging) => {
  storage.set(stateKey, params);
  const { page, rows, sort, filters } = params;

  return confirmations.query({ ...serializers.transform(filters, 'pr', 'jq'), sort: serializers.sorter(sort), skip: (page * rows), take: rows });
};

export default memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const types: Record<number, string> = useMemo(() => ({
    1: t('app.confirmation.register'),
    2: t('app.confirmation.email'),
    3: t('app.confirmation.phone'),
    4: t('app.confirmation.signin'),
    5: t('app.confirmation.password'),
    6: t('app.confirmation.resource'),
    7: t('app.confirmation.unregister'),
    8: t('app.confirmation.update'),
  }), [t]);
  const statuses: Record<number, string> = useMemo(() => ({
    0: t('app.status.rejected'),
    1: t('app.status.waiting'),
    2: t('app.status.approved'),
  }), [t]);

  const { loading, data, total, params, reload } = useLoader(loader);

  const actions: Record<string, (item: IConfirmation) => Promise<any> | void> = useMemo(() => ({
    approve: (item) => dialog.confirm({
      message: t('confirmations.approve.question', { ...item }),
      accept: { label: t('app.approve'), className: 'ml-2 p-button-danger' },
      reject: { label: t('app.cancel') },
    }).then((accepted) => {
      if (accepted) confirmations.approve(item).then(({ success }) => {
        if (success) dialog.toast({ severity: 'success', message: t(`confirmations.approve.success`, { ...item }) });

        return reload(params);
      });
    }),
    reject: (item) => dialog.show({ key: 'confirmation.reject', closable: false,
      header: t('confirmations.title'),
      template: (<Rejection data={{ ...item }} onDone={(data) => {
        dialog.hide('confirmation.reject');
        if (data) return reload(params);
      }} />),
    }),
    info: (item) => dialog.show({ key: 'confirmation.info', closable: true,
      header: t('confirmations.title'),
      template: (<>
        <div className="w-full p-3 flex align-items-start border-round" style={{ minWidth: '24rem', borderRadius: '10px' }}>
          <Icon name="mdi-info" className="m-1 mr-3 flex-none" />
          <span className="flex flex-column">
            <span className="mb-3 font-bold">Reject Reason</span>
            <span className="m-0 text-secondary">{ item.reason }</span>
          </span>
        </div>
        <div className="mt-4 flex justify-content-end">
          <Button text label={t('app.ok')} onClick={() => dialog.hide('confirmation.info')} />
        </div>
      </>),
    }),
  }), [params, reload, t]);

  const table: ITable<IConfirmation> = { data, total, loading,
    className: ['min-h-0', 'flex-auto'],
    columns: [
      { field: 'profile', type: 'string', header: t('confirmations.fields.profile'), className: 'p-0',
        template: ({ profile }) => {
          if (!profile) return;

          const button: IButton = { text: true, href: `/profiles/${profile.id}`, className: 'px-3 py-2',
            icon: (profile.type === 2 ? 'mdi-business' : 'mdi-account-circle'),
            label: `${profile.firstName} ${profile.lastName}`,
          };

          return <Button { ...button } />;
        },
      },
      { field: 'type', type: 'number', header: { label: t('confirmations.fields.type'), className: 'w-16rem' }, filter: true,
        source: { type: 'static',
          data: [
            { value: 1, label: types[1] },
            { value: 2, label: types[2] },
            { value: 3, label: types[3] },
            { value: 4, label: types[4] },
            { value: 5, label: types[5] },
            { value: 6, label: types[6] },
            { value: 7, label: types[7] },
            { value: 8, label: types[8] }
          ],
        },
        // template: (item) => types[item.type],
      },
      { field: 'timestamp', type: 'date', header: { label: t('confirmations.fields.timestamp'), className: 'w-10rem' }, sortable: true },
      { field: 'expires', type: 'date', header: { label: t('confirmations.fields.expires'), className: 'w-10rem' } },
      { field: 'status', type: 'int', header: { label: t('confirmations.fields.status'), className: 'w-6rem' }, filter: true,
        source: { type: 'static',
          data: [
            { value: 0, label: statuses[0] },
            { value: 1, label: statuses[1] },
            { value: 2, label: statuses[2] },
          ],
        },
        // template: (item) => statuses[item.status],
      },
      { field: 'operatorId', type: 'number', header: { label: t('confirmations.fields.operator'), className: 'w-20rem' }, filter: true,
        source: { type: 'lookup', key: 'users',
          params: { role: 'administrator' },
        },
        // template: (item) => types[item.type],
      },
      { field: 'responseDate', type: 'date', header: { label: t('confirmations.fields.responseDate'), className: 'w-10rem' } },
      { field: 'actions', header: { className: 'w-9rem' }, className: 'p-0',
        template: (item) => (<>
          <Button href={`${item.id}`} text rounded icon="mdi-visibility" />
          { item.status === 1 ? <>
            <Button severity="success" text rounded icon="mdi-check" onClick={() => actions['approve'](item)} />
            <Button severity="danger" text rounded icon="mdi-close" onClick={() => actions['reject'](item)} />
          </> : <>
            <Button severity="info" text rounded icon="mdi-info" onClick={() => actions['info'](item)} />
          </> }
        </>),
      },
    ],
    dataKey: 'id',
    removableSort: true,
    resizableColumns: true,
    scrollable: true,
    scrollHeight: 'flex',
    selectionMode: 'single',
    paginator: { show: true, page: params?.page, rows: params?.rows, options: [20, 50, 100], className: 'border-none' },
    filters: params?.filters,
    sort: params?.sort,

    onLoad: reload,
    onRowDoubleClick: (item) => navigate(item.id),
  };

  useMountEffect(() => {
    reload(storage.get(stateKey) ?? { page: 0, rows: 20, sort: { field: 'timestamp', order: 'desc' }, filters: {
      status: { value: 1 }
    } }).then();
  });

  return (
    <div className="h-full flex flex-column">
      <div className="flex flex-none surface-200">
        <Breadcrumb className="flex-auto border-none border-noround bg-transparent" />
        <div className="flex flex-none m-2">
          <Button text icon="mdi-cached" severity="success" className="px-3 py-1" disabled={loading} onClick={() => reload(params)} />
        </div>
      </div>
      <Table { ...table } />
    </div>
  );
});
